import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { api } from "common";
import { useTranslation } from "react-i18next";
import {
  Box,
  Button,
  Grid,
  Typography
} from "@mui/material";
import ChatIcon from "@mui/icons-material/Chat";
import DownloadIcon from "@mui/icons-material/Download";
import moment from "moment";
import jsPDF from "jspdf";
import CircularLoading from "../components/CircularLoading";
import BookingDataLoadError from "../components/BookingDataLoadError";
import { FONT_FAMILY, labelText } from "../common/sharedFunctions";
import localization from "../components/Table/Localization";
import AdminDataTable from "../components/AdminDataTable";
import AdminStatusBadge from "../components/AdminStatus";
import { EmptyState, PageHeader, SectionHeader, StatusBadge, SurfaceCard } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";

const cleanFileName = (value) => String(value || "booking").replace(/[^a-z0-9-_]+/gi, "-");

const getMessageStamp = (message) => {
  return message.createdAt ? moment(message.createdAt).format("lll") : `${message.msgDate || ""} ${message.msgTime || ""}`.trim();
};

const addPdfText = (doc, text, x, y, maxWidth, lineHeight) => {
  const lines = doc.splitTextToSize(String(text || ""), maxWidth);
  doc.text(lines, x, y);
  return y + (lines.length * lineHeight);
};

const ensurePdfSpace = (doc, y, neededHeight) => {
  if (y + neededHeight <= 282) return y;
  doc.addPage();
  return 18;
};

const getMessageTime = (message) => {
  if (!message) return 0;
  const createdAt = message.createdAt ? new Date(message.createdAt).getTime() : 0;
  if (createdAt) return createdAt;
  if (message.msgDate && message.msgTime) {
    const dateParts = String(message.msgDate).split(":");
    if (dateParts.length === 3) {
      const parsed = new Date(`${dateParts[2]}-${dateParts[0]}-${dateParts[1]} ${message.msgTime}`).getTime();
      if (parsed) return parsed;
    }
  }
  return 0;
};

const getSenderLabel = (message, booking, t) => {
  if (message.source === "customer" || message.from === booking.customer) {
    return booking.customer_name || t("customer");
  }
  if (message.source === "driver" || message.from === booking.driver) {
    return booking.driver_name || t("driver");
  }
  return message.source || message.from || t("unknown");
};

export default function AdminChats() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir();
  const label = (key, fallback) => labelText(t, key, fallback);
  const dispatch = useDispatch();
  const { fetchChatMessages, stopFetchMessages } = api;
  const bookinglistdata = useSelector((state) => state.bookinglistdata);
  const chatdata = useSelector((state) => state.chatdata);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const bookings = useMemo(() => bookinglistdata.bookings || [], [bookinglistdata.bookings]);
  const messages = useMemo(() => {
    return (chatdata.messages || []).slice().sort((a, b) => getMessageTime(a) - getMessageTime(b));
  }, [chatdata.messages]);

  useEffect(() => {
    if (!selectedBooking && bookings.length > 0) {
      setSelectedBooking(bookings[0]);
      setSelectedRow(0);
    }
  }, [bookings, selectedBooking]);

  useEffect(() => {
    if (selectedBooking && selectedBooking.id) {
      dispatch(fetchChatMessages(selectedBooking.id));
      return () => dispatch(stopFetchMessages(selectedBooking.id));
    }
  }, [dispatch, fetchChatMessages, stopFetchMessages, selectedBooking]);

  const columns = [
    { title: t("booking_ref"), field: "reference" },
    { title: t("booking_date"), field: "bookingDate", render: rowData => rowData.bookingDate ? moment(rowData.bookingDate).format("lll") : null },
    { title: t("customer"), field: "customer_name" },
    { title: t("driver"), field: "driver_name" },
    { title: t("booking_status_web"), field: "status", render: rowData => <AdminStatusBadge status={rowData.status}>{t(rowData.status)}</AdminStatusBadge> },
  ];

  const downloadConversation = () => {
    if (!selectedBooking) return;
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const fileName = `chat-proof-${cleanFileName(selectedBooking.reference || selectedBooking.id)}.pdf`;
    let y = 16;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.text(label("conversation_proof"), 105, y, { align: "center" });

    y += 10;
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    doc.text(`${t("booking_ref")}: ${selectedBooking.reference || selectedBooking.id}`, 14, y);
    y += 6;
    doc.text(`${t("customer")}: ${selectedBooking.customer_name || "-"}`, 14, y);
    y += 6;
    doc.text(`${t("driver")}: ${selectedBooking.driver_name || "-"}`, 14, y);
    y += 6;
    doc.text(`${t("generated_at")}: ${moment().format("lll")}`, 14, y);
    y += 8;

    doc.setDrawColor(80, 80, 80);
    doc.line(14, y, 196, y);
    y += 8;

    messages.forEach((message, index) => {
      const sender = getSenderLabel(message, selectedBooking, t);
      const stamp = getMessageStamp(message);
      const messageId = message.smsId || `${index + 1}`;
      const header = `${index + 1}. ${sender} | ${stamp} | ID: ${messageId}`;
      const messageLines = doc.splitTextToSize(String(message.message || ""), 170);
      y = ensurePdfSpace(doc, y, 14 + (messageLines.length * 5));

      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      y = addPdfText(doc, header, 14, y, 182, 5);
      y += 2;
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      y = addPdfText(doc, message.message || "", 20, y, 170, 5);
      y += 4;
    });

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(`${label("conversation_proof")} - ${selectedBooking.reference || selectedBooking.id} - ${i}/${pageCount}`, 105, 290, { align: "center" });
    }

    doc.save(fileName);
  };

  if (bookinglistdata.error && bookinglistdata.error.flag) {
    return <BookingDataLoadError message={bookinglistdata.error.msg} />;
  }

  return bookinglistdata.loading ? (
    <CircularLoading />
  ) : (
    <Box className="garahex-admin-chats-page" sx={{ direction: isRTL === "rtl" ? "rtl" : "ltr" }}>
      <PageHeader
        eyebrow={label("communications", "Communications")}
        title={label("admin_chat_proof", "Admin Chats")}
        description={label("admin_chat_proof_description", "Review booking-linked customer and driver messages with the existing conversation proof export.")}
        actions={<StatusBadge tone="purple">{`${bookings.length} ${label("bookings", "bookings")}`}</StatusBadge>}
      />
      <Grid container spacing={2.2} alignItems="stretch">
        <Grid item xs={12} md={5} lg={4}>
          <SurfaceCard sx={{ height: "100%" }}>
            <SectionHeader
              title={label("conversations", "Conversations")}
              description={label("conversations_description", "Select a booking to view its existing message stream.")}
            />
          <AdminDataTable
            title={label("admin_chat_proof", "Admin Chats")}
            columns={columns}
            data={bookings}
            emptyTitle={label("no_conversations_found", "No conversations found")}
            emptyDescription={label("no_conversations_found_description", "No booking conversations are available in the current communication scope.")}
            onRowClick={(event, rowData) => {
              setSelectedBooking(rowData);
              setSelectedRow(rowData.tableData.id);
            }}
            actions={[
              {
                icon: "chat",
                tooltip: t("view_chat"),
                onClick: (event, rowData) => {
                  setSelectedBooking(rowData);
                  setSelectedRow(rowData.tableData.id);
                },
              }
            ]}
            style={{
              direction: isRTL === "rtl" ? "rtl" : "ltr",
            }}
            options={{
              exportButton: false,
              maxColumnSort: "all_columns",
              rowStyle: (rowData) => ({
                backgroundColor: selectedRow === rowData.tableData.id ? "rgba(11, 124, 255, 0.09)" : "#FFFFFF"
              }),
              maxBodyHeight: "calc(100vh - 220px)",
            }}
            localization={localization(t)}
          />
          </SurfaceCard>
        </Grid>
      <Grid item xs={12} md={7} lg={8}>
        <SurfaceCard
          sx={{
            minHeight: "calc(100vh - 130px)",
            overflow: "hidden",
            p: 0,
            display: "flex",
            flexDirection: "column"
          }}
        >
          <Box
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
              background: `linear-gradient(135deg, ${adminTokens.colors.deepNavy}, ${adminTokens.colors.royalBlue})`,
              color: "#FFFFFF",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <ChatIcon />
              <Typography variant="h6" sx={{ fontFamily: FONT_FAMILY }}>
                {label("conversation_proof", "Conversation Proof")}
              </Typography>
            </Box>
            <Button
              variant="contained"
              size="small"
              startIcon={<DownloadIcon />}
              disabled={!selectedBooking || messages.length === 0}
              onClick={downloadConversation}
            >
              {label("download_proof", "Download Proof")}
            </Button>
          </Box>
          {selectedBooking ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle1" sx={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>
                {selectedBooking.reference || selectedBooking.id}
              </Typography>
              <Typography variant="body2" sx={{ fontFamily: FONT_FAMILY, color: adminTokens.colors.muted }}>
                {`${selectedBooking.customer_name || t("customer")} / ${selectedBooking.driver_name || t("driver")}`}
              </Typography>
            </Box>
          ) : null}
          <Box sx={{ px: 2, pb: 2, maxHeight: "calc(100vh - 270px)", overflowY: "auto", flex: 1 }}>
            {chatdata.loading ? (
              <CircularLoading />
            ) : messages.length > 0 && selectedBooking ? (
              messages.map((message, index) => {
                const isCustomer = message.source === "customer" || message.from === selectedBooking.customer;
                return (
                  <Box
                    key={message.smsId || index}
                    sx={{
                      display: "flex",
                      justifyContent: isCustomer ? "flex-start" : "flex-end",
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        maxWidth: "78%",
                        p: 1.6,
                        borderRadius: isCustomer ? "18px 18px 18px 6px" : "18px 18px 6px 18px",
                        backgroundColor: isCustomer ? "#FFFFFF" : "rgba(11, 124, 255, 0.10)",
                        border: `1px solid ${adminTokens.colors.border}`,
                        boxShadow: "0 12px 28px rgba(3, 29, 54, 0.08)",
                      }}
                    >
                      <Typography variant="caption" sx={{ display: "block", fontFamily: FONT_FAMILY, fontWeight: "bold" }}>
                        {getSenderLabel(message, selectedBooking, t)}
                      </Typography>
                      <Typography variant="body1" sx={{ fontFamily: FONT_FAMILY, wordBreak: "break-word", whiteSpace: "pre-wrap" }}>
                        {message.message}
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 1, fontFamily: FONT_FAMILY, color: adminTokens.colors.muted }}>
                        {message.createdAt ? moment(message.createdAt).format("lll") : `${message.msgDate || ""} ${message.msgTime || ""}`.trim()}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            ) : (
              <EmptyState
                title={selectedBooking ? t("chat_not_found") : t("select_booking_to_view_chat")}
                description={selectedBooking ? label("chat_not_found_description", "This booking has no messages in the current chat stream.") : label("select_booking_to_view_chat_description", "Choose a booking from the conversations list to review messages.")}
              />
            )}
          </Box>
        </SurfaceCard>
      </Grid>
    </Grid>
    </Box>
  );
}
